import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip } from '@mui/material'
import { useGridApiContext } from '@mui/x-data-grid-pro'
import React, { useEffect, useMemo, useState } from 'react'

import type { EditorComponentProps } from './registry'

type IconEntry = { cssClass: string; label: string; search: string[] }

const cache: Map<string, IconEntry[]> = new Map()

const fetchIcons = async (endpoint: string): Promise<IconEntry[]> => {
	const hit = cache.get(endpoint)
	if (hit !== undefined) {
		return hit
	}
	const res = await fetch(endpoint, { credentials: 'same-origin' })
	if (!res.ok) {
		throw new Error(`Icon fetch failed: HTTP ${res.status}`)
	}
	const json = (await res.json()) as { icons: IconEntry[] }
	cache.set(endpoint, json.icons)
	return json.icons
}

export const IconPickerEditor: React.FC<EditorComponentProps> = (params) => {
	const apiRef = useGridApiContext()
	const endpoint = params.componentOptions.endpoint as string
	const [open, setOpen] = useState(false)
	const [icons, setIcons] = useState<IconEntry[]>([])
	const [query, setQuery] = useState('')
	const [pending, setPending] = useState<string | null>(null)
	const value = (params.value ?? '') as string

	useEffect(() => {
		if (!open) {
			return undefined
		}
		let cancelled = false
		void fetchIcons(endpoint).then((list) => {
			if (!cancelled) {
				setIcons(list)
			}
		})
		return () => {
			cancelled = true
		}
	}, [endpoint, open])

	const filtered = useMemo(() => {
		if (query.trim() === '') {
			return icons
		}
		const q = query.toLowerCase()
		return icons.filter((i) => i.label.toLowerCase().includes(q) || i.search.some((t) => t.toLowerCase().includes(q)))
	}, [icons, query])

	const commit = (cssClass: string): void => {
		void apiRef.current.setEditCellValue({ id: params.id, field: params.field, value: cssClass })
		setOpen(false)
	}

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
			{value !== '' && <i className={`fal ${value}`} style={{ fontSize: 18 }} />}
			<Button
				size="small"
				onClick={() => {
					setPending(value)
					setOpen(true)
				}}
			>
				{value === '' ? 'Pick icon' : 'Change'}
			</Button>
			{value !== '' && (
				<Tooltip title="Clear">
					<Button size="small" color="error" onClick={() => commit('')}>
						×
					</Button>
				</Tooltip>
			)}
			<Dialog
				open={open}
				onClose={() => {
					setOpen(false)
					setPending(null)
				}}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>Choose icon</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						size="small"
						fullWidth
						placeholder="Search…"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						sx={{ mb: 2 }}
					/>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
							gap: 1,
							maxHeight: 480,
							overflow: 'auto',
						}}
					>
						{filtered.map((icon) => (
							<Box
								key={icon.cssClass}
								onClick={() => setPending(icon.cssClass)}
								sx={{
									border: '1px solid',
									borderColor: pending === icon.cssClass ? 'primary.main' : 'divider',
									borderRadius: 1,
									p: 1,
									textAlign: 'center',
									cursor: 'pointer',
									'&:hover': { borderColor: 'primary.light' },
								}}
							>
								<i className={`fal ${icon.cssClass}`} style={{ fontSize: 24 }} />
								<Box sx={{ fontSize: 11, mt: 0.5 }}>{icon.label}</Box>
							</Box>
						))}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setOpen(false)
							setPending(null)
						}}
					>
						Cancel
					</Button>
					<Button variant="contained" disabled={pending === null} onClick={() => pending !== null && commit(pending)}>
						Select
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	)
}
